/**
 * 乾隆甲子（1744）实测黄道二十八宿宿距，单位为 360 今度。
 *
 * 原表：高鲁《星象统笺》（1933）所录“黄赤道今率宿钤表（一）”，
 * PDF 物理页 13、书内页 11，“乾隆甲子实测／黄道”列。
 * 原始记录精确到度、分、秒；decimalDegrees 只作绘图换算。
 *
 * 注意：原表柳宿“黄道宿距”格清楚印作 15°58′56″，但同表柳、星两行的
 * “开始宫度”相减为 16°58′56″，且只有后者能使 28 宿合计 360°。
 * 用户已裁定采用 16°58′56″：同表开始宫度是观测直接记录、相减自洽，且 28 宿
 * 合计必须为 360°。本模块仍保留印刷原值并将来源冲突标为 pending，等待取得
 * 《御定仪象考成》原刻本对应页复核；不把采用值冒充成原表直录。
 */

export const XIU_SOURCE = Object.freeze({
  system: '乾隆甲子实测黄道宿距',
  epoch: 1744,
  unit: '360今度',
  work: '星象统笺',
  table: '黄赤道今率宿钤表（一）',
  column: '乾隆甲子实测／黄道',
  pdfPage: 13,
  printedPage: 11,
  issues: Object.freeze(['柳宿宿距印作15°58′56″，与同表开始宫度及360°合计相矛盾'])
});

const RAW_XIU_DEGREES = [
  ['角', 10, 39, 19], ['亢', 10, 36, 0],  ['氐', 17, 50, 45], ['房', 4, 51, 59],
  ['心', 8, 15, 0],   ['尾', 15, 11, 23], ['箕', 8, 54, 55],  ['斗', 23, 53, 15],
  ['牛', 7, 40, 9],   ['女', 11, 40, 16], ['虚', 9, 57, 54],  ['危', 20, 6, 57],
  ['室', 15, 40, 37], ['壁', 13, 16, 18], ['奎', 11, 31, 51], ['娄', 12, 57, 48],
  ['胃', 12, 29, 34], ['昴', 9, 2, 50],   ['毕', 15, 14, 7],  ['觜', 0, 59, 27],
  ['参', 10, 36, 25], ['井', 30, 26, 34], ['鬼', 4, 34, 19],
  ['柳', 15, 58, 56, Object.freeze({
    status: 'source-conflict-pending',
    adopted: Object.freeze([16, 58, 56]),
    evidence: '已裁定采用16°58′56″：印刷宿距格为15°58′56″；同表直接观测的开始宫度柳午6°44′03″至星午23°42′59″之差为16°58′56″，且采用值使总和为360°；待原刻本复核'
  })],
  ['星', 8, 25, 34],  ['张', 18, 3, 10],  ['翼', 16, 59, 15], ['轸', 13, 5, 23]
];

export const XIU_GROUPS = Object.freeze({
  东方苍龙: Object.freeze('角亢氐房心尾箕'.split('')),
  北方玄武: Object.freeze('斗牛女虚危室壁'.split('')),
  西方白虎: Object.freeze('奎娄胃昴毕觜参'.split('')),
  南方朱雀: Object.freeze('井鬼柳星张翼轸'.split(''))
});

export const XIU_DEGREES = Object.freeze(RAW_XIU_DEGREES.map(
  ([name, degrees, minutes, seconds, issue = null]) => {
    const original = Object.freeze({ degrees, minutes, seconds });
    const [adoptedDegrees, adoptedMinutes, adoptedSeconds] = issue?.adopted
      ?? [degrees, minutes, seconds];
    const adopted = Object.freeze({
      degrees: adoptedDegrees,
      minutes: adoptedMinutes,
      seconds: adoptedSeconds
    });
    return Object.freeze({
      name,
      original,
      adopted,
      sourceIssue: issue ? Object.freeze({
        status: issue.status,
        evidence: issue.evidence
      }) : null,
      decimalDegrees: adoptedDegrees + adoptedMinutes / 60 + adoptedSeconds / 3600
    });
  }
));

/** 《星象统笺》宿距印刷格的逐行字面合计（柳宿印刷15°时只有359°）。 */
export const XIU_PRINTED_TOTAL_DEGREES = XIU_DEGREES.reduce(
  (sum, { original }) => sum + original.degrees + original.minutes / 60 + original.seconds / 3600,
  0
);

export const XIU_TOTAL_DEGREES = XIU_DEGREES.reduce(
  (sum, { decimalDegrees }) => sum + decimalDegrees,
  0
);

/** 转录硬闸：28 宿必须齐全、顺序正确，且总宿距为 360 今度。 */
export function validateXiuDegrees(entries = XIU_DEGREES, tolerance = 0.01) {
  const expected = Object.values(XIU_GROUPS).flat();
  if (!Array.isArray(entries) || entries.length !== 28) {
    throw new RangeError(`二十八宿宿度必须正好 28 项，实际 ${entries?.length ?? '非数组'}`);
  }
  const names = entries.map(({ name }) => name);
  if (names.join('') !== expected.join('')) {
    throw new RangeError(`二十八宿顺序错误：${names.join('')}`);
  }
  entries.forEach(({ name, original, adopted, sourceIssue, decimalDegrees }) => {
    const values = [
      original?.degrees, original?.minutes, original?.seconds,
      adopted?.degrees, adopted?.minutes, adopted?.seconds,
      decimalDegrees
    ];
    if (!values.every(Number.isFinite) || decimalDegrees <= 0
      || original.minutes < 0 || original.minutes >= 60
      || original.seconds < 0 || original.seconds >= 60
      || adopted.minutes < 0 || adopted.minutes >= 60
      || adopted.seconds < 0 || adopted.seconds >= 60) {
      throw new RangeError(`${name}宿度记录无效`);
    }
    const converted = adopted.degrees + adopted.minutes / 60 + adopted.seconds / 3600;
    if (Math.abs(converted - decimalDegrees) > 1e-10) {
      throw new RangeError(`${name}宿十进制度与采用度分秒不一致`);
    }
    const sourceMatchesAdopted = original.degrees === adopted.degrees
      && original.minutes === adopted.minutes
      && original.seconds === adopted.seconds;
    if (!sourceMatchesAdopted && sourceIssue?.status !== 'source-conflict-pending') {
      throw new RangeError(`${name}宿印刷值与采用值不同却未标注待裁定冲突`);
    }
  });
  const total = entries.reduce((sum, { decimalDegrees }) => sum + decimalDegrees, 0);
  if (Math.abs(total - 360) > tolerance) {
    throw new RangeError(`二十八宿宿距合计 ${total.toFixed(6)}°，偏离 360° 超过 ${tolerance}°`);
  }
  return total;
}

// 模块载入即校验，任何一行转录错误都会阻止视觉层继续使用这份数据。
validateXiuDegrees();
